import React, { useEffect } from "react";
import { Box, Fab, Typography, SvgIcon } from "@mui/material";
import { Add, ErrorOutline } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSavedPosts } from "../../redux/post/postSlice";
import PostGrid from "../../components/posts/PostGrid";
import EmptyState from "../../components/shared/EmptyState/EmptyState";
import { API_CALL_STATUS } from "../../assets/constants/constants";

const SavedPosts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const savedPosts = useSelector((state) => state.post.savedPosts);
  const apiCallStatus = useSelector((state) => state.post.status);

  const getAllSavedPosts = async () => {
    await dispatch(getSavedPosts());
  };

  useEffect(() => {
    getAllSavedPosts();
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: { xs: "calc(100vh - 56px)", md: "calc(100vh - 64px)" },
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {/* Page Header - fixed at top */}
      <Box
        sx={{
          flex: "0 0 auto",
          p: { xs: 2, md: 3 },
          pb: 2,
          borderRadius: "4px",
          backgroundColor: "#001d3d",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          mx: { xs: 2, md: 3 },
          mt: { xs: 2, md: 3 },
        }}
      >
        <Typography
          variant="h4"
          fontWeight={600}
          gutterBottom
          sx={{ color: "#fff", fontSize: "1.5rem" }}
        >
          Saved Posts
        </Typography>
        <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}>
          {savedPosts?.length || 0} {savedPosts?.length === 1 ? "post" : "posts"} saved
        </Typography>
      </Box>

      {/* Scrollable area - only this section scrolls */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          p: { xs: 2, md: 3 },
          pt: 2,
        }}
      >
        {/* Error State */}
        {apiCallStatus === API_CALL_STATUS.FAILED && savedPosts?.length === 0 && (
          <EmptyState
            title="Error loading saved posts"
            description="Please try again later"
            icon={<ErrorOutline sx={{ fontSize: 50, color: "#d32f2f" }} />}
            bgColor="#fff"
          />
        )}

        {/* Empty State */}
        {apiCallStatus === API_CALL_STATUS.SUCCEEDED && (!savedPosts || savedPosts.length === 0) && (
          <EmptyState
            title="No saved posts yet"
            description="Save posts you're interested in to find them here later"
            icon={
              <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 50, color: "#001d3d" }}>
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </SvgIcon>
            }
            bgColor="#fff"
          />
        )}

        {/* Posts Grid - no border */}
        {savedPosts?.length > 0 && (
          <Box sx={{ p: 0 }}>
            <PostGrid posts={savedPosts} />
          </Box>
        )}
      </Box>

      {/* Floating Action Button */}
      <Fab
        aria-label="add"
        sx={{
          position: "fixed",
          bottom: 32,
          right: 32,
          backgroundColor: "#001d3d",
          color: "#fff",
          "&:hover": {
            backgroundColor: "#002d5a",
          },
          transition: "all 0.3s ease",
        }}
        onClick={() => navigate("/fmr/create-post")}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default SavedPosts;